<template>
  <div class="grid gap-4 min-[2048px]:grid-cols-3 xl:grid-cols-2">
    <BaseCard class="flex flex-col gap-4 p-6">
      <!-- Card header -->
      <div class="flex justify-between">
        <TextSubtitle>Gebruiker</TextSubtitle>
        <BaseButton v-if="!editingUser" plain small @click="editingUser = true">
          Wijzigen
        </BaseButton>
        <BaseButton
          v-else
          plain
          small
          class="text-danger!"
          @click="cancelEditingUser"
        >
          Annuleer
        </BaseButton>
      </div>
      <div class="flex max-w-96 flex-col gap-4">
        <!-- Full name -->
        <form @submit.prevent>
          <InputField
            v-model="fullName"
            label="Naam"
            :disabled="!editingUser || fullnamePending"
          >
            <template #action>
              <BaseButton
                v-if="fullName !== userStore.fullName"
                color="primary"
                :loading="fullnamePending"
                @click="submitFullName"
              >
                Wijzig
              </BaseButton>
            </template>
          </InputField>
        </form>
        <!-- Email -->
        <form @submit.prevent>
          <InputField
            v-model="email"
            type="email"
            label="E-mailadres"
            :state-error="validEmailError"
            :helper-text="validEmailError ? 'Ongeldig e-mailadres' : null"
            :disabled="!editingUser || emailPending || emailSent"
            @blur="showEmailValidation = true"
          >
            <template #action>
              <BaseTooltip
                v-if="email !== userStore.email"
                description="De wijziging wordt definitief gemaakt via een bevestingsemail op het nieuwe adres."
              >
                <BaseButton
                  color="primary"
                  :loading="emailPending"
                  class="w-25"
                  @click="submitEmailRequest"
                >
                  Bevestig
                </BaseButton>
              </BaseTooltip>
            </template>
          </InputField>
        </form>
        <MessageComponent v-if="emailSent" in-card>
          <p>
            Er is een bevestigingsmail verstuurd naar
            <span class="font-bold">{{ emailSent }}</span
            >. Gebruik de link in de mail om het nieuwe e-mailadres te
            bevestigen.
          </p>
        </MessageComponent>
      </div>
    </BaseCard>
    <!-- Password -->
    <BaseCard ref="passwordCardRef" class="flex flex-col gap-4 p-6">
      <!-- Card header -->
      <div class="flex justify-between">
        <TextSubtitle>Wachtwoord</TextSubtitle>
        <BaseButton
          v-if="!editingPassword"
          plain
          small
          @click="startEditingPassword"
        >
          Wachtwoord wijzigen
        </BaseButton>
        <BaseButton
          v-else
          plain
          small
          class="text-danger!"
          @click="cancelEditingPassword"
        >
          Annuleer
        </BaseButton>
      </div>
      <!-- Current password placeholder -->
      <div v-if="!editingPassword" class="flex max-w-96 flex-col gap-4">
        <InputField
          placeholder="&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;"
          label="Wachtwoord"
          disabled
          class="max-w-96"
        />
      </div>
      <!-- Password change form -->
      <form v-else class="flex max-w-96 flex-col gap-4" @submit.prevent>
        <InputField
          v-model="currentPassword"
          type="password"
          label="Huidig wachtwoord"
          :state-error="passwordIncorrect"
          :helper-text="passwordIncorrect ? 'Wachtwoord is onjuist' : null"
          :disabled="passwordPending"
          class="max-w-96"
        />
        <!-- Password instructions -->
        <div class="flex flex-col gap-2">
          <p>
            Kies een wachtwoord dat je nergens anders gebruikt met de volgende
            eigenschappen:
          </p>
          <ul class="list-inside list-disc">
            <li>Bevat minimaal 8 tekens</li>
            <li>Mag niet enkel uit cijfers bestaan</li>
            <li>Geen veelvoorkomend wachtwoord</li>
            <li>Mag niet op het e-mailadres lijken</li>
          </ul>
        </div>
        <InputField
          v-model="newPassword"
          type="password"
          label="Nieuw wachtwoord"
          :state-error="passwordIllegal"
          :helper-text="
            passwordIllegal ? 'Wachtwoord voldoet niet aan de eisen' : null
          "
          :disabled="passwordPending"
          class="max-w-96"
        />
        <InputField
          v-model="newPasswordRepeated"
          type="password"
          label="Herhaal nieuw wachtwoord"
          :helper-text="
            passwordRepeatError ? 'Wachtwoorden komen niet overeen' : null
          "
          :state-error="passwordRepeatError"
          :disabled="passwordPending"
          class="max-w-96"
        />
        <BaseButton
          color="primary"
          :loading="passwordPending"
          class="ml-auto"
          @click="submitNewPassword"
        >
          Wijzig
        </BaseButton>
      </form>
    </BaseCard>
    <!-- Other -->
    <BaseCard class="flex flex-col gap-4 p-6">
      <TextSubtitle>Overige</TextSubtitle>
      <SwitchComponent
        v-if="userStore.hasFlag('can-use-anonymize')"
        v-model="viewStore.anonymize"
      >
        Anonimiseer namen en locaties van portfolios en installaties
      </SwitchComponent>
      <RouterLink :to="{ name: 'logout' }">
        <BaseButton plain class="text-danger! w-full">Uitloggen</BaseButton>
      </RouterLink>
    </BaseCard>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from "vue"
import { useRouter } from "vue-router"
import HTTP_STATUS from "@/helpers/httpStatus.js"
import { triggerEvent } from "@/services/plausibleService.js"
import useViewStore from "@/stores/viewStore.js"
import useNotificationStore from "@/stores/notificationStore.js"
import useUserStore from "@/stores/userStore.js"
import useSessionStore from "@/stores/sessionStore.js"
import {
  TextSubtitle,
  BaseCard,
  BaseButton,
  BaseTooltip,
  InputField,
} from "@repowerednl/ui-component-library"
import MessageComponent from "@/components/information/MessageComponent.vue"
import SwitchComponent from "@/components/input/SwitchComponent.vue"

const router = useRouter()
const viewStore = useViewStore()
const notificationStore = useNotificationStore()
const userStore = useUserStore()
const sessionStore = useSessionStore()
const passwordCardRef = ref()
const editingUser = ref(false)
const editingPassword = ref(false)

const fullName = ref(userStore.fullName)
const fullnamePending = ref(false)

const email = ref(userStore.email)
const showEmailValidation = ref(false)
const emailPending = ref(false)
const emailSent = ref(false)

const currentPassword = ref("")
const newPassword = ref("")
const newPasswordRepeated = ref("")
const passwordPending = ref(false)
const passwordIncorrect = ref(false)
const passwordIllegal = ref(false)
const showPasswordRepeatValidation = ref(false)

const validEmail = computed(() => {
  // This pattern should match the one used in the 'validate_email()' function
  // in the 'repower-django' backend.
  const emailPattern = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,7}\b/
  return email.value.match(emailPattern)
})

const validEmailError = computed(
  () => showEmailValidation.value && !validEmail.value,
)

const passwordRepeatError = computed(
  () =>
    showPasswordRepeatValidation.value &&
    newPassword.value !== newPasswordRepeated.value,
)

function cancelEditingUser() {
  editingUser.value = false
  fullName.value = userStore.fullName
  fullnamePending.value = false
  email.value = userStore.email
  showEmailValidation.value = false
  emailPending.value = false
}

function startEditingPassword() {
  editingPassword.value = true
  nextTick(() => {
    passwordCardRef.value.$el.scrollIntoView({ behavior: "smooth" })
  })
}

function cancelEditingPassword() {
  editingPassword.value = false
  currentPassword.value = ""
  newPassword.value = ""
  newPasswordRepeated.value = ""
  passwordPending.value = false
  passwordIncorrect.value = false
  passwordIllegal.value = false
  showPasswordRepeatValidation.value = false
}

function submitFullName() {
  fullnamePending.value = true
  userStore.changeFullName(
    fullName.value,
    () => {
      cancelEditingUser()
      notificationStore.pushToast(
        "Naam gewijzigd",
        `Je naam is gewijzigd naar ${fullName.value}`,
      )
    },
    (error) => {
      fullnamePending.value = false
      notificationStore.pushError(
        "Fout bij het wijzigen van de naam",
        `De naamswijziging kon niet worden doorgevoerd (code: ${error.code}).`,
        "full-name-change-error",
      )
    },
  )
  triggerEvent("account:change-full-name")
}

function submitEmailRequest() {
  if (!validEmail.value) {
    return
  }
  emailPending.value = true
  userStore.requestEmailChange(
    email.value,
    () => {
      emailSent.value = email.value
      cancelEditingUser()
      notificationStore.pushToast(
        "E-mailadreswijziging aangevraagd",
        "Er is een e-mail verstuurd naar het nieuwe adres ter bevestiging.",
        "email-change-success",
      )
    },
    (error) => {
      emailPending.value = false
      switch (error.code) {
        case HTTP_STATUS.BAD_REQUEST:
          notificationStore.pushError(
            "Ongeldig e-mailadres",
            "Gebruik een geldig e-mailadres.",
            "email-change-invalid",
          )
          break
        case HTTP_STATUS.CONFLICT:
          notificationStore.pushError(
            "E-mailadres niet beschikbaar",
            "Het opgegeven e-mailadres kan niet worden gebruikt voor dit account.",
            "email-change-unavailable",
          )
          break
        default:
          notificationStore.pushError(
            "Fout bij het wijzigen van het e-mailadres",
            `Het e-mailadres kon niet worden gewijzigd (code: ${error.code}).`,
            "email-change-error",
          )
      }
    },
  )
  triggerEvent("account:request-email-change")
}

function submitNewPassword() {
  showPasswordRepeatValidation.value = true
  if (newPassword.value !== newPasswordRepeated.value) {
    return
  }
  passwordPending.value = true
  userStore.changePassword(
    currentPassword.value,
    newPassword.value,
    () => {
      cancelEditingPassword()
      sessionStore.setAuthenticated(false)
      router.push({ name: "logout" })
      notificationStore.pushToast(
        "Wachtwoord gewijzigd",
        "Log in met je nieuwe wachtwoord.",
        "password-change-success",
      )
    },
    (error) => {
      passwordPending.value = false
      switch (error.code) {
        case HTTP_STATUS.UNAUTHORIZED:
          passwordIncorrect.value = true
          break
        case HTTP_STATUS.BAD_REQUEST:
          passwordIllegal.value = true
          break
        case HTTP_STATUS.TOO_MANY_REQUESTS:
          notificationStore.pushError(
            "Te veel pogingen",
            "Je hebt te vaak geprobeerd je wachtwoord te wijzigen. Probeer het later opnieuw.",
            "password-change-throttle",
          )
          break
        default:
          notificationStore.pushError(
            "Fout bij het wijzigen van het wachtwoord",
            `Het wachtwoord kon niet worden gewijzigd (code: ${error.code}).`,
            "password-change-error",
          )
      }
    },
  )
  triggerEvent("account:change-password-from-settings")
}

watch(
  () => newPassword.value,
  () => (passwordIllegal.value = false),
)
</script>
